import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Key, Save, RotateCcw, CheckCircle2, AlertCircle, Eye, EyeOff,
  Sparkles, Mail, CreditCard, Lock, Loader2, ShieldCheck, BadgeCheck,
  RefreshCw, Database, Server, Info
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GROUP_META = {
  ai: { label: "AI & LLM", icon: Sparkles, color: "#D4859B", desc: "Memory Curator & Bouquet Builder AI" },
  email: { label: "Email", icon: Mail, color: "#C5DB7B", desc: "Notifikasi transaksional (Resend)" },
  payment: { label: "Pembayaran", icon: CreditCard, color: "#8DBBA0", desc: "Gateway Midtrans (Indonesia)" },
  auth: { label: "Autentikasi", icon: Lock, color: "#B89BD4", desc: "Google OAuth login" },
  misc: { label: "Lainnya", icon: Key, color: "#E8A87C", desc: "Kunci layanan lain" },
};

const LABEL = "block text-[10px] tracking-widest uppercase text-[#8A857D] mb-1.5";
const FIELD = "w-full px-3 py-2.5 bg-[#FDF8F6] border border-[#D4859B]/20 rounded-xl text-sm font-body font-mono text-[#1C1A19] outline-none focus:border-seikat-pink/50";

export default function AdminApiKeys() {
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [revealed, setRevealed] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [testResult, setTestResult] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/api-keys`, { withCredentials: true });
      setItems(r.data.items || []);
      setDrafts({});
    } catch {
      toast.error("Gagal memuat API keys");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const dirty = Object.keys(drafts).length > 0;

  const handleSave = async () => {
    const payload = { items: Object.entries(drafts).map(([slug, value]) => ({ slug, value })) };
    if (!payload.items.length) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/api-keys`, payload, { withCredentials: true });
      toast.success("API keys berhasil disimpan");
      await load();
    } catch {
      toast.error("Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async (slug) => {
    if (!window.confirm("Hapus key ini dari DB? (env fallback akan digunakan jika tersedia)")) return;
    try {
      await axios.put(`${API}/admin/api-keys`, { items: [{ slug, value: "" }] }, { withCredentials: true });
      toast.success("Key dihapus dari DB");
      await load();
    } catch { toast.error("Gagal menghapus"); }
  };

  const handleTest = async (slug) => {
    setTesting(prev => ({ ...prev, [slug]: true }));
    setTestResult(prev => ({ ...prev, [slug]: null }));
    try {
      const r = await axios.post(`${API}/admin/api-keys/${slug}/test`, {}, { withCredentials: true });
      setTestResult(prev => ({ ...prev, [slug]: r.data }));
    } catch (e) {
      setTestResult(prev => ({ ...prev, [slug]: { ok: false, message: e.response?.data?.detail || "Test gagal" } }));
    } finally {
      setTesting(prev => ({ ...prev, [slug]: false }));
    }
  };

  const grouped = items.reduce((acc, it) => {
    const g = it.group || "misc";
    if (!acc[g]) acc[g] = [];
    acc[g].push(it);
    return acc;
  }, {});

  const configuredCount = items.filter(i => i.source !== "none").length;
  const dbCount = items.filter(i => i.source === "db").length;

  return (
    <div data-testid="admin-api-keys">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl text-[#1C1A19]">API <span className="text-seikat-pink">Keys</span></h1>
          <p className="font-body text-sm text-[#8A857D] mt-1">Konfigurasi kunci integrasi LLM, email, payment, dan OAuth</p>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-50 border border-green-200 text-xs font-body text-green-700">
              <ShieldCheck size={11} /> Terenkripsi di DB (Fernet)
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-seikat-pink/8 border border-seikat-pink/20 text-xs font-body text-seikat-pink">
              <Database size={11} /> {dbCount} key di DB
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FDF8F6] border border-[#D4859B]/20 text-xs font-body text-[#6A6560]">
              <Server size={11} /> {configuredCount}/{items.length} terkonfigurasi
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="w-9 h-9 rounded-xl border border-[#D4859B]/20 flex items-center justify-center text-[#8A857D] hover:text-seikat-pink transition-colors">
            <RefreshCw size={15} />
          </button>
          {dirty && (
            <button onClick={() => setDrafts({})} className="flex items-center gap-1.5 px-3 py-2 border border-[#D4859B]/20 rounded-xl text-sm font-body text-[#4A453F] hover:bg-[#FDF8F6] transition-colors">
              <RotateCcw size={13} /> Batal
            </button>
          )}
          {dirty && (
            <button onClick={handleSave} disabled={saving} data-testid="api-keys-save"
              className="flex items-center gap-1.5 px-4 py-2 bg-seikat-pink text-white rounded-xl text-sm font-body font-medium hover:bg-seikat-pink/90 transition-colors disabled:opacity-60">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              Simpan ({Object.keys(drafts).length})
            </button>
          )}
        </div>
      </div>

      {/* Info banner */}
      <div className="bg-[#FDF8F6] border border-[#D4859B]/20 rounded-2xl p-4 mb-8 flex gap-3">
        <Info size={16} className="text-seikat-pink flex-shrink-0 mt-0.5" />
        <p className="font-body text-sm text-[#4A453F]">
          Nilai yang diisi di sini disimpan <strong>terenkripsi di database</strong> dan menggantikan nilai dari file <code className="bg-[#EDE8E3] px-1.5 py-0.5 rounded text-xs font-mono">.env</code>.
          Jika DB kosong untuk suatu key, sistem otomatis fallback ke <code className="bg-[#EDE8E3] px-1.5 py-0.5 rounded text-xs font-mono">.env</code>.
          Kolom <strong>Emergent LLM Key</strong> sudah diisi secara default dari sistem.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 size={24} className="animate-spin text-seikat-pink" /></div>
      ) : (
        <div className="space-y-10">
          {Object.entries(grouped).map(([groupKey, groupItems]) => {
            const meta = GROUP_META[groupKey] || GROUP_META.misc;
            return (
              <section key={groupKey} data-testid={`api-keys-group-${groupKey}`}>
                <div className="flex items-center gap-3 mb-5">
                  <span className="w-8 h-px" style={{ background: meta.color }} />
                  <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: `${meta.color}22` }}>
                    <meta.icon size={13} style={{ color: meta.color }} />
                  </div>
                  <h2 className="font-heading text-xl text-[#1C1A19]">{meta.label}</h2>
                  <span className="font-body text-xs text-[#A8A29A]">{meta.desc}</span>
                </div>
                <div className="grid sm:grid-cols-1 lg:grid-cols-2 gap-4">
                  {groupItems.map(item => (
                    <KeyCard
                      key={item.slug}
                      item={item}
                      draft={drafts[item.slug]}
                      revealed={revealed[item.slug]}
                      testing={testing[item.slug]}
                      testResult={testResult[item.slug]}
                      onDraftChange={(val) => setDrafts(prev => ({ ...prev, [item.slug]: val }))}
                      onClearDraft={() => {
                        const next = { ...drafts };
                        delete next[item.slug];
                        setDrafts(next);
                      }}
                      onToggleReveal={() => setRevealed(prev => ({ ...prev, [item.slug]: !prev[item.slug] }))}
                      onTest={() => handleTest(item.slug)}
                      onClear={() => handleClear(item.slug)}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {/* Floating save bar when dirty */}
      <AnimatePresence>
        {dirty && (
          <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-[#1C1A19] rounded-2xl px-5 py-3 flex items-center gap-4 shadow-2xl"
            data-testid="api-keys-save-bar"
          >
            <p className="font-body text-sm text-white">
              <span className="text-seikat-lime font-medium">{Object.keys(drafts).length}</span>
              <span className="ml-1 text-white/60"> perubahan belum disimpan</span>
            </p>
            <button onClick={() => setDrafts({})} className="text-xs font-body text-white/50 hover:text-white transition-colors" data-testid="api-keys-cancel">
              Batal
            </button>
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 bg-seikat-pink text-white rounded-xl text-xs font-body font-medium hover:bg-seikat-pink/90 transition-colors disabled:opacity-60"
              data-testid="api-keys-save-float">
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              Simpan Semua
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function KeyCard({ item, draft, revealed, testing, testResult, onDraftChange, onClearDraft, onToggleReveal, onTest, onClear }) {
  const hasDraft = draft !== undefined;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className="bg-white rounded-2xl border border-[#D4859B]/12 shadow-sm p-5"
      data-testid={`api-key-card-${item.slug}`}
    >
      {/* Title row */}
      <div className="flex items-start justify-between gap-2 mb-4">
        <div className="min-w-0 flex-1">
          <h3 className="font-heading text-lg text-[#1C1A19] leading-tight">{item.label}</h3>
          <p className="font-body text-[11px] text-[#8A857D] mt-0.5">{item.hint}</p>
          {item.env_var && (
            <code className="inline-block mt-1.5 text-[10px] font-mono text-[#6A6560] bg-[#F0ECE8] px-1.5 py-0.5 rounded">
              {item.env_var}
            </code>
          )}
        </div>
        <SourceBadge source={item.source} />
      </div>

      {/* Current value */}
      <div className="mb-4">
        <label className={`block text-[10px] tracking-widest uppercase mb-1.5 ${item.source === "none" ? "text-[#C4B8AF]" : "text-[#8A857D]"}`}>
          Nilai Saat Ini
        </label>
        <div className={`px-3 py-2.5 rounded-xl font-mono text-xs min-h-[38px] break-all ${
          item.source === "none"
            ? "bg-[#FAF8F6] border border-dashed border-[#D4859B]/20 text-[#C4B8AF] italic"
            : "bg-[#FDF8F6] border border-[#D4859B]/12 text-[#4A453F]"
        }`}>
          {item.preview || <span className="text-[#C4B8AF] not-italic font-body text-xs">— belum dikonfigurasi —</span>}
        </div>
      </div>

      {/* Input for new value */}
      <div className="mb-4">
        <label className="block text-[10px] tracking-widest uppercase text-[#8A857D] mb-1.5">
          {hasDraft ? "Nilai Baru (belum disimpan)" : "Ubah Nilai"}
        </label>
        <div className="relative">
          <input
            type={revealed ? "text" : "password"}
            value={hasDraft ? draft : ""}
            onChange={(e) => onDraftChange(e.target.value)}
            placeholder={item.is_plain ? "Nilai teks biasa..." : "Paste kunci baru..."}
            className={`${FIELD} pr-20 ${hasDraft ? "border-seikat-pink/50 bg-seikat-pink/3" : ""}`}
            data-testid={`api-key-input-${item.slug}`}
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            <button onClick={onToggleReveal} className="w-7 h-7 rounded-full hover:bg-[#F0ECE8] flex items-center justify-center text-[#A8A29A] hover:text-[#4A453F] transition-colors" title="Tampilkan/sembunyikan">
              {revealed ? <EyeOff size={12} /> : <Eye size={12} />}
            </button>
            {hasDraft && (
              <button onClick={onClearDraft} className="w-7 h-7 rounded-full hover:bg-seikat-pink/10 flex items-center justify-center text-[#A8A29A] hover:text-seikat-pink transition-colors" title="Batalkan perubahan">
                <RotateCcw size={12} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between gap-2">
        <button onClick={onTest} disabled={testing}
          className="flex items-center gap-1.5 text-xs font-body text-[#4A453F] hover:text-seikat-pink transition-colors"
          data-testid={`api-key-test-${item.slug}`}>
          {testing ? <Loader2 size={11} className="animate-spin" /> : <BadgeCheck size={11} />}
          Test Koneksi
        </button>
        {item.configured_in_db && (
          <button onClick={onClear} className="text-[10px] tracking-widest uppercase text-[#C4B8AF] hover:text-red-500 transition-colors font-body" data-testid={`api-key-clear-${item.slug}`}>
            Hapus dari DB
          </button>
        )}
      </div>

      {/* Test result */}
      {testResult && (
        <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
          className={`mt-3 p-3 rounded-xl flex items-start gap-2 text-xs font-body ${
            testResult.ok
              ? "bg-green-50 border border-green-200 text-green-700"
              : "bg-red-50 border border-red-200 text-red-700"
          }`}
          data-testid={`api-key-result-${item.slug}`}
        >
          {testResult.ok ? <CheckCircle2 size={12} className="mt-0.5 flex-shrink-0" /> : <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />}
          <span>{testResult.message}</span>
        </motion.div>
      )}
    </motion.div>
  );
}

function SourceBadge({ source }) {
  if (source === "db") return (
    <span className="flex-shrink-0 inline-flex items-center gap-1 text-[9px] tracking-widest uppercase px-2 py-1 rounded-full bg-green-50 text-green-700 border border-green-200 whitespace-nowrap" data-testid="source-badge-db">
      <Database size={9} /> DB
    </span>
  );
  if (source === "env") return (
    <span className="flex-shrink-0 inline-flex items-center gap-1 text-[9px] tracking-widest uppercase px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 whitespace-nowrap" data-testid="source-badge-env">
      <Server size={9} /> ENV
    </span>
  );
  return (
    <span className="flex-shrink-0 inline-flex items-center gap-1 text-[9px] tracking-widest uppercase px-2 py-1 rounded-full bg-[#FAF8F6] text-[#A8A29A] border border-[#D4859B]/15 whitespace-nowrap" data-testid="source-badge-none">
      <Key size={9} /> Kosong
    </span>
  );
}
